/**
 * Die kundensichere Produktuebersicht.
 *
 *   Produktkopf   Bild, Nummer, Produktidentitaet, Einordnung, Listenpreis
 *   Technik       drei Saeulen (ArticleTechnical)
 *   Beschreibung  nur, wenn es eine gibt
 *
 * DIESE ANSICHT IST KEINE FEHLERLISTE (Auftrag V2, Abschnitt 2). Sie enthaelt
 * keine Warnhinweise, keinen Pruefstand, keine Provenienz und keine
 * Beschaffungsangabe. Nicht, weil das unwichtig waere, sondern weil sie auf
 * einem Tablet vor einem Kunden stehen darf: ein Produkt, das intern noch
 * Datenpflege braucht, ist deswegen kein fehlerhaftes Produkt.
 *
 * Was hier NICHT vorkommt und wo es stattdessen lebt:
 *
 *   Warnungen, offene Aufgaben  -> im jeweils zustaendigen Arbeitsbereich
 *   Quellbezeichnung, Herkunft  -> Herkunft & Historie
 *   Pruefstand je Merkmal       -> Technische Details
 *   Lieferant, MOQ, Konditionen -> Einkauf
 *   Kartonmasse, CBM, Zolltarif -> Logistik & Verpackung
 */
import { useEffect, useState } from "react";
import type { ArticleDetail, ArticleDossier, MetaResponse, ResolvedText,
              TextLanguageCode } from "@homebase/shared";
import { api } from "../lib/api.ts";
import { TEXT_LANGUAGE_CODES } from "@homebase/shared";
import { useI18n, type I18n } from "../i18n/index.tsx";
import { familyLabel } from "../i18n/terminology.ts";
import { ArticleTechnical } from "./ArticleTechnical.tsx";
import { articleImageSrc } from "../lib/images.ts";
import "./ArticleOverview.css";
import { verfuegbarkeitswort } from "../lib/verfuegbarkeit.ts";

/**
 * ERKENNEN -- Produktkopf und Handlungsbedarf.
 *
 * Bewusst getrennt vom Rest: dieser Teil bleibt stehen, AUCH wenn jemand
 * einen Arbeitsbereich oeffnet. Wer in der Verpackungshistorie arbeitet, soll
 * nicht raten muessen, an welchem Artikel er das tut.
 */
export function ArticleIdentity({ a, texts }: {
  a: ArticleDetail;
  texts: readonly ResolvedText[] | null;
}): React.JSX.Element {
  const i18n = useI18n();
  return <ProductHead a={a} texts={texts} i18n={i18n} />;
}

/**
 * VERSTEHEN -- die technische Uebersicht und die Beschreibung.
 *
 * Das ist der Standardinhalt der Seite. Oeffnet jemand einen Arbeitsbereich,
 * tritt dieser an seine Stelle -- nicht darunter. Beides gleichzeitig zu
 * zeigen hiesse, dieselben Merkmale zweimal auf einem Bildschirm zu haben.
 */
export function ArticleOverview({ a, meta, texts }: {
  a: ArticleDetail;
  meta: MetaResponse;
  /** Die aufgeloesten Texte. null solange sie laden. */
  texts: readonly ResolvedText[] | null;
}): React.JSX.Element {
  const i18n = useI18n();
  /*
   * DIE UEBERSICHT LIEST NICHT NUR MERKMALSWERTE.
   *
   * Welcher Treiber verbaut ist, steht in `article_relations`; wie viele
   * Leuchten an einen Leitungsschutzschalter passen, in
   * `circuit_breaker_capacities`. Beides ist technisches Produktwissen und
   * gehoert in die Uebersicht -- es ist nur kein Merkmalswert und war
   * deshalb bisher nirgends zu sehen.
   *
   * Gelesen wird die BESTEHENDE Aktenantwort. Es entsteht keine zweite
   * Datenwahrheit und keine zweite Abfrage neben ihr.
   */
  const [akte, setAkte] = useState<ArticleDossier | null>(null);
  useEffect(() => {
    let weg = false;
    api.articleDossier(a.id).then((d) => { if (!weg) setAkte(d); }).catch(() => undefined);
    return () => { weg = true; };
  }, [a.id]);

  return (
    <>
      <Bestand a={a} i18n={i18n} />
      <ArticleTechnical articleId={a.id} meta={meta}
                        core={a.core.filter((c) => c.order >= 1).map((c) => c.definition)} />
      <Bestueckung akte={akte} i18n={i18n} />
      <OverviewTexts texts={texts} i18n={i18n} />
    </>
  );
}

/**
 * DIE OPERATIVE ZEILE -- vier Zahlen ueber der Bereichsleiste.
 *
 * WOFUER SIE DA IST
 *
 * „Habe ich das da? Wird es gefragt? Ist etwas unterwegs? Wann kommt es an?"
 * Das sind die vier Fragen, die vor jeder Zusage stehen, und sie gehoeren
 * zum ARTIKEL -- nicht in einen Reiter, den man erst aufschlagen muss. Bis
 * zum 17.09.2026 standen sie in der Uebersicht und waren damit genau dann
 * fort, wenn jemand in der Technik, im Einkauf oder in der Logistik
 * arbeitete.
 *
 * WARUM SIE KEINE KACHELN SIND
 *
 * Vier grosse Zahlen mit Farben lesen sich wie ein Armaturenbrett und
 * verdraengen die Produktidentitaet, ueber der sie stehen. Sie sind deshalb
 * eine Zeile: kleine Beschriftung, Wert daneben, dezente Trenner. Wer sie
 * braucht, findet sie; wer das Produkt liest, wird nicht von ihnen
 * angesprungen.
 *
 * LEERE IST EINE AUSSAGE, NULL IST EINE ANDERE
 *
 * Ohne Recht auf Beschaffungsdaten liefert der Server `stock: null` und
 * `incoming: null` -- das heisst „geht dich nichts an" und nicht „es ist
 * nichts da". Fehlt der Wert, steht „nicht bestimmt". Eine Null wird nur
 * gezeigt, wenn sie wirklich gemeldet wurde.
 */
export function OperativeZeile({ a, i18n }: {
  a: ArticleDetail; i18n: I18n;
}): React.JSX.Element | null {
  const t = i18n.t;
  const zahl = (v: string | number): string =>
    Number.isFinite(Number(v))
      ? Number(v).toLocaleString(i18n.lang, { maximumFractionDigits: 3 })
      : String(v);
  const unbestimmt = <span className="opz__leer">{t("opz.undetermined")}</span>;

  /*
   * BESTAND -- UND ZWAR MIT DEM WORT, DAS DIE LISTE AUCH BENUTZT.
   *
   * Hier stand fest „Verfuegbar" als Beschriftung. Dieser Fehler war schon
   * einmal behoben: produktiv steht TP154 mit Bestand 0, die Liste sagte
   * „Nicht verfuegbar" und die Akte im selben Augenblick „Verfuegbar · 0
   * Stueck". Nicht weil die Daten auseinanderliefen -- beide lasen dieselbe
   * Sicht --, sondern weil die Akte gar keine Aussage machte und trotzdem
   * wie eine aussah.
   *
   * Beim Umzug in diese Zeile ist die Korrektur verlorengegangen. Sie steht
   * jetzt wieder hier, aus derselben Funktion wie in der Liste.
   *
   * UND DER STAND GEHOERT DAZU. Ein Lagerbericht ist ein Foto, kein Fenster.
   * Ohne Datum liest ihn jeder als „jetzt", und daraus entstehen Zusagen,
   * die das Lager nicht deckt. In einer Zeile ist dafuer kein Platz -- er
   * steht deshalb als Hinweis am Wert, sichtbar beim Zeigen und lesbar fuer
   * Vorlesewerkzeuge.
   */
  const s = a.operations.stock;
  const bestandswort = verfuegbarkeitswort(a.operationalState.availability, t);
  const verfuegbar = s
    ? <span>
        <strong>{zahl(s.quantity)}</strong> {s.unit === "Stueck" ? t("stock.pieces") : s.unit}
        {/* Leise, aber lesbar -- nicht als Hinweisblase. Was nur beim Zeigen
            erscheint, steht in einem Ausdruck nicht mit und wird beim
            Vorlesen uebersprungen. */}
        <span className="opz__stand">
          {" · "}{t("stock.asOf")} {new Date(s.asOf).toLocaleDateString(i18n.lang)}
          {" · "}{t("stock.source")} {i18n.tCode("stockSource", s.source, s.source)}
        </span>
      </span>
    : unbestimmt;

  /*
   * ANGEBOTE DER LETZTEN ZWOELF MONATE -- ANZAHL UND MENGE.
   *
   * Hier stand ein Halbjahr. greenius verkauft an Planer und Betreiber, und
   * zwischen einem Angebot und einer Bestellung liegen oft mehr als sechs
   * Monate; ein Halbjahr unterschaetzte die Nachfrage genau bei den
   * Projekten, die am groessten sind. Zwoelf Monate sind die ruhigere Zahl.
   *
   * Neben der ANZAHL steht die angebotene MENGE (0057). Sie ist nur dann eine
   * Zahl, wenn JEDES Angebot des Zeitraums eine Menge traegt: eine Summe aus
   * einem Teil der Angebote saehe genauso aus wie die ganze und waere um den
   * Rest zu niedrig. Die vorhandenen Angebote tragen keine Menge -- dort steht
   * deshalb „nicht bestimmt", nicht 0.
   *
   * Das Halbjahr bleibt als Zusatzauskunft im Hinweis erreichbar, steht aber
   * nicht als zweite Kennzahl daneben: zwei Zeitraeume nebeneinander machen
   * aus einer Zahl eine Rechenaufgabe.
   */
  const TAG = 24 * 60 * 60 * 1000;
  const jahr = Date.now() - 365 * TAG;
  const halbjahr = Date.now() - 182 * TAG;
  const imJahr = a.operations.offers.filter((o) => new Date(o.offeredAt).getTime() >= jahr);
  const imHalbjahr = imJahr.filter((o) => new Date(o.offeredAt).getTime() >= halbjahr).length;
  const mengeBekannt = imJahr.every((o) => o.quantity !== null);
  const menge = mengeBekannt
    ? <><strong>{zahl(imJahr.reduce((n, o) => n + Number(o.quantity), 0))}</strong> {t("stock.pieces")}</>
    : unbestimmt;
  const angebote = a.operations.offers.length === 0
    ? unbestimmt
    : <span title={t("opz.offersHalfYear", { n: imHalbjahr })}>
        <strong>{imJahr.length}</strong>{" / "}{menge}
      </span>;

  /*
   * BESTELLT UND ZULAUF stammen aus derselben Quelle: den offenen Zulaeufen.
   * „Bestellt" ist ihre Summe, „ETA LG" der fruehste erwartete Termin --
   * der naechste, der ankommt, ist der, auf den jemand wartet.
   */
  const zulauf = a.operations.incoming;
  const bestellt = zulauf === null ? unbestimmt
    : zulauf.length === 0 ? unbestimmt
    : <><strong>{zahl(zulauf.reduce((n, z) => n + Number(z.quantity), 0))}</strong> {t("stock.pieces")}</>;
  const termine = (zulauf ?? []).map((z) => z.expectedAt).filter((d): d is string => d !== null).sort();
  const eta = termine.length > 0
    ? <strong>{new Date(termine[0]!).toLocaleDateString(i18n.lang)}</strong>
    : unbestimmt;

  return (
    <div className="opz" aria-label={t("opz.aria")}>
      <span className="opz__feld"><span className="opz__label">{bestandswort}</span>{verfuegbar}</span>
      <span className="opz__feld"><span className="opz__label">{t("opz.offers")}</span>{angebote}</span>
      <span className="opz__feld"><span className="opz__label">{t("opz.ordered")}</span>{bestellt}</span>
      <span className="opz__feld"><span className="opz__label">{t("opz.eta")}</span>{eta}</span>
    </div>
  );
}

/**
 * DER VERFUEGBARE BESTAND.
 *
 * Operative Information, keine Produkteigenschaft: sie steht deshalb in der
 * Uebersicht und nicht bei den Merkmalen. Ein Bestand veraltet, ein
 * Lichtstrom nicht.
 *
 * DREI FAELLE, DIE VERSCHIEDEN AUSSEHEN MUESSEN
 *
 *   Menge vorhanden      „Verfuegbar 263 Stueck", mit Quelle und Stand.
 *   Menge = 0            „Verfuegbar 0 Stueck" -- eine Aussage, kein Mangel.
 *                        Sie wird NICHT als fehlende Angabe dargestellt.
 *   keine Zeile          Es erscheint gar nichts. Eine Null zu zeigen waere
 *                        eine Behauptung, die keine Quelle deckt.
 *
 * Der dritte Fall ist auch der Fall des Betrachters ohne `purchasing.read`:
 * der Server liefert ihm `stock: null`. Die Mengen sind Innenansicht, der
 * abgeleitete Zustand nicht -- diese Grenze bestand vor diesem Block und
 * bleibt unveraendert.
 *
 * WARUM DER STAND IMMER DABEISTEHT
 *
 * Ein Lagerbericht ist ein Foto, kein Fenster. Ohne Datum liest ihn jeder als
 * „jetzt", und genau daraus entstehen Zusagen, die das Lager nicht deckt.
 */
function Bestand({ a, i18n }: { a: ArticleDetail; i18n: I18n }): React.JSX.Element | null {
  const s = a.operations.stock;
  if (!s) return null;
  /*
   * SORTIMENTSSTATUS UEBER DEM BESTAND -- und nicht aus ihm abgeleitet.
   *
   * Ein Lagerartikel mit leerem Regal darf nicht aussehen wie ein
   * ausgelisteter. Die Zeile sagt deshalb zuerst, ob der Artikel gefuehrt
   * wird, und erst danach, was gerade da ist. Ist der Sortimentsstatus nicht
   * bestimmt, steht das da -- erfunden wird er nicht.
   */
  const t = i18n.t;
  /* Ganze Stueck ohne Nachkommastellen, Meterware mit -- die Menge ist
     NUMERIC(14,3), weil nicht alles in Stueck gefuehrt wird. */
  const menge = Number(s.quantity);
  const text = Number.isFinite(menge)
    ? menge.toLocaleString(i18n.lang, { maximumFractionDigits: 3 })
    : s.quantity;
  return (
    <section className="overview__stock" aria-label={t("stock.title")}>
      <h3 className="overview__stocktitle">
        {a.lifecycleStatus
          ? i18n.tCode("lifecycle", a.lifecycleStatus)
          : t("list.lifecycleUnset")}
        {" · "}{t("stock.title")}
      </h3>
      <dl className="overview__stocklist">
        {/*
          * DIE VERFUEGBARKEIT IST EINE AUSSAGE, KEINE UEBERSCHRIFT.
          *
          * Hier stand fest `t("stock.available")` -- "Verfügbar" -- als
          * Beschriftung ueber der Mengenzeile. Gemeint war "verfuegbare
          * Menge". Gelesen wurde "dieser Artikel ist verfuegbar". Solange
          * eine Zahl ueber null danebenstand, fiel der Unterschied nicht auf.
          *
          * Produktiv stand TP154 mit Bestand 0: die Liste sagte "Nicht
          * verfügbar", die Akte im selben Moment "Verfügbar · 0 Stück". Nicht
          * weil die Daten auseinanderliefen -- beide lasen dieselbe Sicht --,
          * sondern weil die Akte gar keine Aussage machte und trotzdem wie
          * eine aussah.
          *
          * Das Wort kommt jetzt aus demselben Feld wie in der Liste und aus
          * derselben Funktion. Die Menge steht unveraendert daneben; sie
          * WIDERSPRICHT dem Wort nicht mehr, sondern begruendet es.
          */}
        <div className="overview__stockmain">
          <dt>{verfuegbarkeitswort(a.operationalState.availability, t)}</dt>
          <dd><strong>{text}</strong> {s.unit === "Stueck" ? t("stock.pieces") : s.unit}</dd>
        </div>
        <div>
          <dt>{t("stock.asOf")}</dt>
          <dd>{new Date(s.asOf).toLocaleString(i18n.lang)}</dd>
        </div>
        <div>
          <dt>{t("stock.source")}</dt>
          <dd>{i18n.tCode("stockSource", s.source, s.source)}</dd>
        </div>
      </dl>
    </section>
  );
}

/**
 * Verbaute Komponenten und Leuchten je Leitungsschutzschalter.
 *
 * KEIN WERT = KEIN ABSCHNITT. Traegt ein Artikel weder eine Beziehung noch
 * eine Schalterangabe, erscheint hier nichts -- kein leerer Block, keine
 * „nicht bestimmt"-Zeile. Die Uebersicht bleibt ruhig.
 */
function Bestueckung({ akte, i18n }: { akte: ArticleDossier | null; i18n: I18n }): React.JSX.Element | null {
  if (!akte) return null;
  const komponenten = akte.relations.filter((r) => r.relationType === "verwendet_komponente");
  if (komponenten.length === 0 && akte.breakerCapacities.length === 0) return null;
  return (
    <section className="overview__parts" aria-label={i18n.t("overview.parts")}>
      <h3 className="overview__partstitle">{i18n.t("overview.parts")}</h3>
      <dl className="overview__partslist">
        {komponenten.map((r) => (
          <div className="overview__part" key={r.id}>
            <dt>{i18n.t("overview.component")}</dt>
            <dd>
              <span className="overview__partnumber">{r.target.articleNumber}</span>
              {r.target.shortDescription && (
                <span className="overview__partdesc">{r.target.shortDescription}</span>
              )}
            </dd>
          </div>
        ))}
        {akte.breakerCapacities.map((c) => (
          <div className="overview__part" key={c.id}>
            <dt>{i18n.t("overview.breaker", { characteristic: c.characteristic, current: c.ratedCurrent })}</dt>
            <dd><strong>{c.maxFixtures}</strong> {i18n.t("overview.breakerUnit")}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

// ── Produktkopf ─────────────────────────────────────────────────────────────

/**
 * Der redaktionelle Artikelname -- oder nichts.
 *
 * Fachentscheidung 17 erlaubte bis V1, ersatzweise die technische
 * Quellbezeichnung im Kopf zu zeigen, solange sie gekennzeichnet war. In
 * einer kundensicheren Ansicht ist auch die gekennzeichnete Fassung falsch:
 * „TP154 | L: 154 cm | 18 - 75 W | ≤ 11.250 lm | nw | IP65 | 3-Phasen | …"
 * ist Provenienz, keine Produktbezeichnung.
 *
 * Sie ist nicht geloescht -- sie steht unveraendert unter „Herkunft &
 * Historie". Fehlt der redaktionelle Name, bleibt der Kopf vorlaeufig
 * reduzierter. Ein erfundener Name waere schlechter als keiner.
 */
function articleName(texts: readonly ResolvedText[] | null, lang: TextLanguageCode): string | null {
  const slot = texts?.find((t) => t.type === "artikelname");
  const body = slot?.text?.locales.find((l) => l.language === lang)?.body;
  return body && body.trim() !== "" ? body : null;
}

/**
 * Der Produktkopf -- drei Felder in einer Zeile.
 *
 *   Bild  |  Identitaet (Nummer, Bezeichnung, Einordnung)  |  Preis & Lage
 *
 * Die frueheren sechs Faktenpaare (Lebenszyklus, Familie, Modell, Variante,
 * Rolle, Lieferant) sind zu EINER Einordnungszeile verdichtet. Sechs
 * Beschriftungen fuer sechs kurze Werte kosten mehr Hoehe als sie tragen --
 * und der Lieferant gehoert ohnehin nicht in eine kundensichere Ansicht.
 */
function ProductHead({ a, texts, i18n }: {
  a: ArticleDetail; texts: readonly ResolvedText[] | null; i18n: I18n;
}): React.JSX.Element {
  const t = i18n.t;
  const name = articleName(texts, i18n.lang === "en" ? "en" : "de");

  /*
   * Die Einordnungszeile. Nur Belegtes -- ein „nicht bestimmt" bekaeme
   * denselben visuellen Rang wie ein echter Wert und waere trotzdem keiner
   * (Abschnitt 6).
   */
  const einordnung: string[] = [];
  if (a.families.length > 0) {
    einordnung.push(a.families.map((f) => familyLabel(i18n, f.code, f.name)).join(" · "));
  }
  if (a.roles.length > 0) {
    einordnung.push(a.roles.map((r) => i18n.tCode("articleRole", r.code, r.name)).join(" · "));
  }
  if (a.lifecycleStatus) einordnung.push(i18n.tCode("lifecycle", a.lifecycleStatus));
  if (a.productModel) {
    einordnung.push(a.variant
      ? t("work.stateModelVariant", { model: a.productModel.name })
      : a.productModel.name);
  }

  return (
    <header className="head">
      <ProductImage src={articleImageSrc(a.families)} i18n={i18n} />

      <div className="head__id">
        <div className="head__kind">{t("head.kind")}</div>
        <h2 className="head__number">
          {a.articleNumber}
          {a.numberIsPlaceholder && (
            <span className="badge badge--placeholder" title={a.placeholderNote ?? ""}>
              {t("models.placeholderBadge")}
            </span>
          )}
        </h2>

        {name !== null && <p className="head__name">{name}</p>}

        {/*
          * DIE KURZBESCHREIBUNG GEHOERT ZUR IDENTITAET.
          *
          * „HB UGR | 120–200 W | 160 lm/W | 4000 K | DALI 2 | 85° | CRI >90"
          * ist der Schluessel, an dem ein Mensch die richtige Ausfuehrung
          * erkennt -- vier Geschwister unterscheiden sich in zwei Zahlen, und
          * genau die stehen hier.
          *
          * Sie stand bisher nur in der Artikelliste. Wer eine Akte geoeffnet
          * hatte, sah die Nummer und musste die Unterscheidung aus den
          * Merkmalen zusammensuchen; am 17.09.2026 fiel das im produktiven
          * Praxistest auf.
          *
          * Untergeordnet, nicht dominant: die Artikelnummer bleibt die
          * Identitaet, die Kurzbeschreibung erklaert sie.
          */}
        {a.shortDescriptionRule !== null && (
          <p className="head__short">{a.shortDescriptionRule}</p>
        )}

        {einordnung.length > 0 && <p className="head__class">{einordnung.join("  ·  ")}</p>}

        {/*
          * ── BESTELLFAEHIGKEIT -- MIT GRUENDEN (§5) ──────────────
          *
          * SIE STEHT IN DER IDENTITAET UND NICHT IN EINER KARTE
          * WEITER UNTEN. „Kann ich das bestellen?" ist eine Frage an
          * den Artikel als Ganzes, und wer sie beantwortet haben
          * will, soll nicht scrollen.
          *
          * UND SIE NENNT DIE GRUENDE. „Nicht bestellfaehig" allein
          * ist eine Sackgasse; „keine Produktmodellzuordnung,
          * Platzhalternummer" ist eine Arbeitsanweisung. Deshalb
          * ALLE Gruende, nicht der erste.
          *
          * BESTELLFAEHIG WIRD NICHT GEFEIERT. Kein gruenes Band,
          * kein Haken ueber die halbe Zeile: der Zustand, der
          * Aufmerksamkeit braucht, ist der andere. „Bestellfaehig"
          * darf leise aussehen.
          */}
        <p className="head__order" data-testid="art-bestellfaehig"
          data-bestellfaehig={String(a.orderability.orderable)}>
          {a.orderability.orderable ? (
            <span className="head__orderok">{t("ord.orderable")}</span>
          ) : (
            <>
              <span className="head__orderno">{t("ord.blocked")}</span>
              <span className="head__ordergruende">
                {a.orderability.blockers
                  .map((b) => i18n.tCode("orderBlock", b)).join(" · ")}
              </span>
            </>
          )}
        </p>
      </div>

      <CommercialBlock preis={a.listPrice} i18n={i18n} />
    </header>
  );
}

/**
 * Das Produktbild -- die Katalogabbildung der Produktfamilie.
 *
 * Nicht je SKU: TP154-4KD2D, TP154-4KD2D-60 und TP154-4KD2D-EMGCY zeigen
 * dasselbe physische Produkt. Der Pfad folgt aus der bereits vorhandenen
 * Familienzuordnung des Artikels (siehe `lib/images.ts`) -- es entsteht keine
 * neue Datenstruktur und keine zweite Bilddatei.
 *
 * Fehlt die Datei, bleibt die Flaeche neutral leer. KEIN kaputtes Bildsymbol
 * und kein erfundenes Platzhalterbild: die Regel deckt heute 12 der 36
 * Familien ab, und die uebrigen sollen als Luecke erkennbar sein.
 */
function ProductImage({ src, i18n }: { src: string | null; i18n: I18n }): React.JSX.Element {
  const [fehlt, setFehlt] = useState(false);

  if (src === null || fehlt) {
    return (
      <div className="head__image" role="img" aria-label={i18n.t("head.imageNone")}>
        <span className="head__imagemark" aria-hidden="true">▤</span>
        <span className="head__imagenote">{i18n.t("head.imageNone")}</span>
      </div>
    );
  }
  return (
    <div className="head__image head__image--photo">
      {/* Kein `loading="lazy"`: das Bild steht im ersten Sichtbereich und
          soll da sein, bevor jemand hinsieht. */}
      <img src={src} alt={i18n.t("head.imageAlt")} onError={() => setFehlt(true)} />
    </div>
  );
}

/**
 * Listenpreis.
 *
 * HOMEBASE fuehrt heute keine Preisspalte -- die 354 Listenpreise liegen in
 * der Masterquelle und sind nie importiert worden. Der Platz steht trotzdem
 * und traegt die groesste Zahl der Seite: er soll aus zwei Metern Abstand
 * lesbar sein, weil genau das der Tabletfall vor einem Kunden ist.
 *
 * Ohne Wert steht ein Gedankenstrich -- und sonst nichts. Der erklaerende
 * Satz aus V1 („Preis und Verfuegbarkeit werden in HOMEBASE noch nicht
 * gefuehrt") war eine interne Auskunft an prominentester Stelle einer
 * kundensicheren Ansicht.
 *
 * WAS HIER NIE STEHEN DARF: EXW, EK, Marge, Einkaufskonditionen. Der
 * Listenpreis ist der Verkaufspreis und das einzige kundensichere Entgelt.
 */
function CommercialBlock({ preis, i18n }: {
  /** `null` = kein Recht auf Preise (purchasing.read); dann bleibt es beim Strich. */
  preis: ArticleDetail["listPrice"];
  i18n: I18n;
}): React.JSX.Element {
  const t = i18n.t;
  const eur = (raw: string): string => `${Number(raw).toLocaleString(
    i18n.lang === "en" ? "en-GB" : "de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  /*
   * DREI ZUSTAENDE, NICHT ZWEI.
   *
   * Bis hierher stand hier immer „—" -- auch wenn HOMEBASE einen Listenpreis
   * berechnet hatte. Das war nicht vorsichtig, sondern falsch: ein
   * Gedankenstrich sagt „nichts da". Jetzt steht, was da ist, und in welchem
   * Zustand:
   *   freigegeben            die kaufmaennische Entscheidung
   *   berechnet, nicht frei  ein Rechenergebnis -- ausdruecklich benannt
   *   nicht berechnet        wirklich nichts
   * Ein berechneter Preis wird dadurch NICHT freigegeben; er steht nur da,
   * wo man ihn sucht, und sagt, dass er es nicht ist.
   */
  const frei = preis?.released ?? null;
  const rechnung = preis?.calculated ?? null;
  return (
    <div className="head__comm">
      <div className="head__pricelabel">{t("head.listPrice")}</div>
      {preis === null ? (
        <div className="head__pricevalue head__pricevalue--none">—</div>
      ) : frei !== null ? (
        <>
          <div className="head__pricevalue">{eur(frei.amountEur)}</div>
          <div className="head__pricestate head__pricestate--frei">
            {t("head.priceReleased", { date: frei.validFrom })}
          </div>
        </>
      ) : rechnung !== null ? (
        <>
          <div className="head__pricevalue head__pricevalue--berechnet">{eur(rechnung.amountEur)}</div>
          <div className="head__pricestate">{t("head.priceCalculated")}</div>
        </>
      ) : (
        <div className="head__pricevalue head__pricevalue--none head__pricevalue--klein">
          {t("head.priceNone")}
        </div>
      )}
    </div>
  );
}

// ── Texte im Ueberblick ─────────────────────────────────────────────────────

const ANRISS = 240;

/**
 * Kurz- und Langbeschreibung, eine Sprache.
 *
 * OHNE TEXT KEIN ABSCHNITT. Bis V1 stand hier ein Leerzustand mit der
 * Aufforderung „Texte verwalten" -- eine Verwaltungsaufgabe an prominenter
 * Stelle einer Produktpraesentation. Gibt es keinen redaktionellen Text,
 * erscheint dieser Bereich gar nicht; bearbeitet wird er im Bereich Texte.
 *
 * Der globale Sprachschalter steuert die OBERFLAECHE. Welche Sprachfassung
 * eines Produkttextes man liest, ist eine fachliche Frage -- deshalb ein
 * eigener, kleiner Schalter. Beide beginnen beim selben Wert, bleiben aber
 * unabhaengig.
 */
function OverviewTexts({ texts, i18n }: {
  texts: readonly ResolvedText[] | null; i18n: I18n;
}): React.JSX.Element | null {
  const t = i18n.t;
  const [lang, setLang] = useState<TextLanguageCode>(i18n.lang === "en" ? "en" : "de");
  const [open, setOpen] = useState(false);

  const body = (type: string, l: TextLanguageCode): string | null => {
    const slot = texts?.find((x) => x.type === type);
    const b = slot?.text?.locales.find((x) => x.language === l)?.body;
    return b && b.trim() !== "" ? b : null;
  };
  const kurz = body("kurzbeschreibung", lang);
  const lang_ = body("langtext", lang);

  /*
   * Der Sprachschalter erscheint nur, wenn es ueberhaupt etwas umzuschalten
   * gibt. Zwei Knoepfe ueber einem leeren Feld sind ein Bedienelement ohne
   * Wirkung.
   */
  const irgendwo = TEXT_LANGUAGE_CODES.some(
    (l) => body("kurzbeschreibung", l) !== null || body("langtext", l) !== null);
  if (!irgendwo) return null;

  return (
    <section className="otexts" aria-label={t("otexts.aria")}>
      <div className="otexts__bar">
        <span className="otexts__title">{t("otexts.title")}</span>
        <div className="otexts__lang" role="group" aria-label={t("otexts.langAria")}>
          {TEXT_LANGUAGE_CODES.map((l) => (
            <button key={l} onClick={() => setLang(l)}
              className={l === lang ? "otexts__langbtn otexts__langbtn--on" : "otexts__langbtn"}
              aria-pressed={l === lang}>
              {i18n.tCode("textLang", l, l)}
            </button>
          ))}
        </div>
      </div>

      {kurz === null && lang_ === null && (
        <p className="otexts__empty">{t("otexts.emptyLanguage")}</p>
      )}
      {kurz !== null && <p className="otexts__short">{kurz}</p>}
      {lang_ !== null && (
        <div className="otexts__long">
          <p>{open || lang_.length <= ANRISS ? lang_ : `${lang_.slice(0, ANRISS).trimEnd()}…`}</p>
          {lang_.length > ANRISS && (
            <button className="linkbtn" onClick={() => setOpen(!open)}>
              {open ? t("otexts.less") : t("otexts.more")}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
